// conversions.ts
import {
    METERS_PER_KM,
    METERS_PER_MILE,
    METERS_PER_FOOT,
    MINUTE,
    HOUR,
    DAY,

} from "./constants.js";
import { safeMultiply, safeDivide } from "./safe_math.js";
import type { CanonTime, CanonDist, DistanceUnit, TimeUnit, ConvertDistance, ConvertTime, ConvertSpeed, OutDist, OutTimeType, OutSpeedType } from './scaleInterfaces.js';
/*───────────────────────────────────────────────────────────────
  🧭 CANONICAL MAPPINGS
───────────────────────────────────────────────────────────────*/

export const DIST_ALIASES: Record<string, CanonDist> = {
    m: "m", meter: "m", meters: "m",
    km: "km", kms: "km", kilometer: "km", kilometers: "km",
    mi: "mi", mile: "mi", miles: "mi",
    ft: "ft", f: "ft", foot: "ft", feet: "ft",
};

export const TIME_ALIASES: Record<string, CanonTime> = {
    s: "s", sec: "s", second: "s", seconds: "s",
    min: "min", m: "min", minute: "min", minutes: "min",
    h: "h", hr: "h", hour: "h", hours: "h",
    day: "day", d: "day", days: "day",
};

export const DIST_FACTORS: Record<CanonDist, number> = {
    m: 1,
    km: METERS_PER_KM,
    mi: METERS_PER_MILE,
    ft: METERS_PER_FOOT,
};

export const TIME_FACTORS: Record<CanonTime, number> = {
    s: 1,
    min: MINUTE,
    h: HOUR,
    day: DAY,
};

/*───────────────────────────────────────────────────────────────
  🔍 CANONICALIZATION HELPERS
───────────────────────────────────────────────────────────────*/

export function canonDist(u: DistanceUnit | string | undefined): CanonDist {
    const key = (u ?? "m").toString().toLowerCase();
    const canon = DIST_ALIASES[key];
    if (!canon) throw new Error(`Unknown distance unit: ${u}`);
    return canon;
}

export function canonTime(u: TimeUnit | string | undefined): CanonTime {
    const key = (u ?? "s").toString().toLowerCase();
    const canon = TIME_ALIASES[key];
    if (!canon) throw new Error(`Unknown time unit: ${u}`);
    return canon;
}

/*───────────────────────────────────────────────────────────────
  ⚖️ NORMALIZATION HELPERS
───────────────────────────────────────────────────────────────*/

export function distanceToMeters(d: number, unit: DistanceUnit | string): number {
    const u = canonDist(unit);
    return safeMultiply(d, DIST_FACTORS[u]);
}
export function metersToDistance(v: number, unit: DistanceUnit | string): number {
    const u = canonDist(unit);
    return safeDivide(v, DIST_FACTORS[u]);
}

export function timeToSeconds(t: number, unit: TimeUnit | string): number {
    const u = canonTime(unit);
    return safeMultiply(t, TIME_FACTORS[u]);
}
export function secondsToTime(v: number, unit: TimeUnit | string): number {
    const u = canonTime(unit);
    return safeDivide(v, TIME_FACTORS[u]);
}

/*───────────────────────────────────────────────────────────────
  🚀 SPEED CONVERSIONS (normalize / unnormalize)
───────────────────────────────────────────────────────────────*/

export function speedToMps(
    v: number,
    distUnit: DistanceUnit | string,
    timeUnit: TimeUnit | string
): number {
    const du = canonDist(distUnit);
    const tu = canonTime(timeUnit);
    return v * (DIST_FACTORS[du] / TIME_FACTORS[tu]);
}

export function mpsToSpeed(
    vMps: number,
    distUnit: DistanceUnit | string,
    timeUnit: TimeUnit | string
): number {
    const du = canonDist(distUnit);
    const tu = canonTime(timeUnit);
    return vMps * (TIME_FACTORS[tu] / DIST_FACTORS[du]);
}

/*───────────────────────────────────────────────────────────────
  🎯 UNIVERSAL CONVERTERS
───────────────────────────────────────────────────────────────*/

export function convertDistance({
    d,
    fromDist,
    toDist,
    vOnly = true,
}: ConvertDistance): OutDist {
    const m = distanceToMeters(d, fromDist);
    const D = canonDist(toDist ?? "m");
    const out = metersToDistance(m, D);
    return vOnly ? out : { d: out, D };
}

export function convertTime({
    t,
    fromTime,
    toTime,
    vOnly = true,
}: ConvertTime): OutTimeType {
    const sec = timeToSeconds(t, fromTime);
    const T = canonTime(toTime ?? "s");
    const out = secondsToTime(sec, T);
    return vOnly ? out : { t: out, T };
}

export function convertSpeed({
    v,
    fromDist,
    fromTime,
    toDist,
    toTime,
    vOnly = true,
}: ConvertSpeed): OutSpeedType {
    const mps = speedToMps(v, fromDist, fromTime);
    const d = canonDist(toDist ?? "m");
    const t = canonTime(toTime ?? "s");
    const out = mpsToSpeed(mps, d, t);
    return vOnly ? out : { v: out, d, t };
}

/*───────────────────────────────────────────────────────────────
  🧮 GENERIC “NORMALIZE / UNNORMALIZE” INTERFACES
───────────────────────────────────────────────────────────────*/

export interface UnitConverter<T> {
    normalize: (value: number, unit: T) => number;
    unnormalize: (value: number, unit: T) => number;
}

export const DistanceConverter: UnitConverter<DistanceUnit> = {
    normalize: distanceToMeters,
    unnormalize: metersToDistance,
};

export const TimeConverter: UnitConverter<TimeUnit> = {
    normalize: timeToSeconds,
    unnormalize: secondsToTime,
};

export const SpeedConverter: UnitConverter<[DistanceUnit, TimeUnit]> = {
    normalize: (v: number, [du, tu]) => speedToMps(v, du, tu),
    unnormalize: (v: number, [du, tu]) => mpsToSpeed(v, du, tu),
};

/*───────────────────────────────────────────────────────────────
  🧩 COMPATIBILITY WRAPPERS (legacy aliases)
───────────────────────────────────────────────────────────────*/

export const toMeters = distanceToMeters;
export const fromMeters = metersToDistance;
export const toSeconds = timeToSeconds;
export const fromSeconds = secondsToTime;

export const velocityToMs = (
    value: number,
    unit: string
): number => speedToMps(value, unit, "s");

export const velocityFromMs = (
    value: number,
    unit: string
): number => mpsToSpeed(value, unit, "s");

/** Non-canonical helper for arbitrary rate conversion, e.g. ft/day → m/s */
export const fromMps = (
    v: number,
    dist_unit: DistanceUnit | string,
    time_unit: TimeUnit | string
): number => mpsToSpeed(v, dist_unit, time_unit);

/*───────────────────────────────────────────────────────────────
  📊 UTILITIES
───────────────────────────────────────────────────────────────*/

export const isFiniteNum = (x: any): x is number => Number.isFinite(x);
export const fmt = (n: number, digits = 2) =>
    isFiniteNum(n) ? n.toFixed(digits) : "N/A";

